/* eslint-disable max-lines-per-function */
import React, { useContext, useEffect, useState } from 'react';
import { CERTIFICATION_CATEGORY, ENABLE_DISABLE_CATEGORY_DIALOG } from '../../Constants/strings';
import DisabledEditIcon from '../../Assets/images/svg/dar_pencil_gray.svg';
import '../CertificationMap/certificationMap.scss';
import '../RoleMaster/roleMaster.scss';
import Filter from './Filter/filters';
import EditIcon from '../../Assets/images/svg/dar_pencil.svg';
import PuzzleLoader from '../../Assets/images/svg/loading puzzle.gif';
import DataGridComponent from '../../Components/DataGrid/dataGrid';
import ControlledSwitches from '../../Components/Switch/switch';
import { AuthContext } from '../../Contexts/AuthContext';
import EditCategoryForm from './EditCategory/editCategory';
import EnableDisableCategoryDialog from './enableDisableCategoryDialog';
import SimpleSnackbar from '../../Components/Snackbar/snackbar';
import { handleDialogSubmit, handleDialogClose } from './enableDisableCategoryDialogUtils';
import { fetchCategoryList } from './certificationCategoryUtil';
import { MODULE_NAMES } from '../../Constants/modules';

const CertificationCategory = () => {
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const { userProfile } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [subCategoryName, setSubCategoryName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [snackBarCss, setSnackBarCss] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [severity, setSeverity] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.MASTER_TRAINING_CERT_CATEGORY] || []
    : [];
  const hasAccess = (action) => {
    return permission.includes(action);
  };

  const checkMobileView = () => {
    setIsMobileView(window.innerWidth < 1024);
  };

  useEffect(() => {
    // API call to fetch category master details
    setLoading(true);
    fetchCategoryList()
      .then(data => {
        setRows(data);
        setNewRows(data); // Assign filteredList the same value as rows
        setLoading(false);
      });
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  useEffect(() => {
    if (selectedCategories.length > 0 || selectedSubCategories.length > 0) {
      const selectedCategoriesName = selectedCategories.map(category => category.name);
      const selectedSubCategoriesNames = selectedSubCategories.map(subCategory => subCategory.name);
      const filtered = rows.filter(item =>
        (selectedCategoriesName.length === 0 || selectedCategoriesName.includes(item.categoryName))
        && (selectedSubCategoriesNames.length === 0 || selectedSubCategoriesNames.includes(item.subCategoryName)),
      );
      setNewRows(filtered);
    } else {
      setNewRows(rows);
    }
  }, [selectedCategories, selectedSubCategories, rows]);

  let updatedRows = [];
  updatedRows = newRows?.map(({ subCategoryId, ...rest }) => ({
    id: subCategoryId,
    ...rest,
  }));

  const updateCategoriesList = () => {
    fetchCategoryList()
      .then(data => {
        setRows(data);
        setNewRows(data); // Ensure state is updated here
      });
    setSelectedCategories([]);
    setSelectedSubCategories([]);
  };


  const editHandleClickOpen = (
    categoryId,
    id,
    categoryNameVal,
    subCategoryNameVal,
    isActiveVal,
  ) => {
    setSelectedCategoryId(categoryId);
    setCategoryName(categoryNameVal);
    setSubCategoryName(subCategoryNameVal);
    setSelectedSubCategoryId(id);
    setIsActive(isActiveVal);
    setOpenEditModal(true);
    setIsEdit(true);
  };

  const handleSwitchChange = (data) => {
    // Actions to be performed after toggling enable/disable switch
    data.last_updated_by = userProfile.user_name;
    setSelectedRole(data); // Set the selected role data
    setIsDialogOpen(true);
    setIsEdit(true);
  };

  const enableDisableCategorySnackbar = (message, severityVal, cssClassName) => {
    setSnackBarCss(cssClassName);
    setOpenSnackbar(true);
    setSnackbarMessage(message);
    setSeverity(severityVal);
    setTimeout(() => {
      setOpenSnackbar(false);
    }, 4000);
  };

  const renderActionCell = (params) => {
    return (
      <div className="mobile-action-icons">
        <ControlledSwitches
          disabled={!hasAccess('delete')} // Disable switch based on permission
          checked={Boolean(params.row.isActive)}
          handleChange={() => {
            handleSwitchChange(params.row);
          }}
          inputProps={{ 'aria-label': 'enable/disable' }}
          switchCss="switchCss"
        />
        <button
          id="roleMaster-edit-button"
          disabled={!hasAccess('update')} // Disable edit button based on permission
          className="role-action-edit-icon"
          onClick={() =>
            editHandleClickOpen(
              params.row.categoryId,
              params.row.id,
              params.row.categoryName,
              params.row.subCategoryName,
              params.row.isActive,
            )
          }
          aria-label="Edit"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: !hasAccess('update') ? 'not-allowed' : 'pointer',
          }}
        >
          <img
            src={!hasAccess('update') ? DisabledEditIcon : EditIcon}
            alt="Edit"
          />
        </button>
      </div>
    );
  };

  const desktopColumns = [
    {
      field: CERTIFICATION_CATEGORY.CATEGORY_TABLE_FIELD.CATEGORY_NAME,
      headerName: CERTIFICATION_CATEGORY.CATEGORY_TABLE_HEADER.CATEGORY,
      flex: 0.25,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CERTIFICATION_CATEGORY.CATEGORY_TABLE_FIELD.SUB_CATEGORY_NAME,
      headerName: CERTIFICATION_CATEGORY.CATEGORY_TABLE_HEADER.SUBCATEGORY,
      flex: 0.25,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CERTIFICATION_CATEGORY.CATEGORY_TABLE_FIELD.IS_ACTIVE,
      headerName: CERTIFICATION_CATEGORY.CATEGORY_TABLE_HEADER.ENABLE_DISABLE,
      flex: 0.25,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div>
          <ControlledSwitches
            disabled={!hasAccess('delete')} // Disable switch based on permission
            checked={Boolean(params.row.isActive)}
            handleChange={() => {
              handleSwitchChange(params.row);
            }}
            inputProps={{ 'aria-label': 'enable/disable' }}
            switchCss="switchCss"
          />
        </div>
      ),
    },
    {
      field: CERTIFICATION_CATEGORY.CATEGORY_TABLE_FIELD.ACTION,
      headerName: CERTIFICATION_CATEGORY.CATEGORY_TABLE_HEADER.ACTION,
      flex: 0.08,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div>
          <button
            id="roleMaster-edit-button"
            disabled={!hasAccess('update')} // Disable edit button based on permission
            className="role-action-edit-icon"
            onClick={() =>
              editHandleClickOpen(
                params.row.categoryId,
                params.row.id,
                params.row.categoryName,
                params.row.subCategoryName,
                params.row.isActive,
              )
            }
            aria-label="Edit"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: !hasAccess('update') ? 'not-allowed' : 'pointer',
            }}
          >
            <img
              src={!hasAccess('update') ? DisabledEditIcon : EditIcon}
              alt="Edit"
            />
          </button>
        </div>
      ),
    },
  ];

  const mobileColumns = [
    {
      field: CERTIFICATION_CATEGORY.CATEGORY_TABLE_FIELD.CATEGORY_NAME,
      headerName: CERTIFICATION_CATEGORY.CATEGORY_TABLE_HEADER.CATEGORY,
      headerClassName: 'role-name-column',
      cellClassName: 'role-name-cell',
      width: 186,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CERTIFICATION_CATEGORY.CATEGORY_TABLE_FIELD.SUB_CATEGORY_NAME,
      headerName: CERTIFICATION_CATEGORY.CATEGORY_TABLE_HEADER.SUBCATEGORY,
      headerClassName: 'description-column',
      cellClassName: 'description-cell',
      width: 740,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: CERTIFICATION_CATEGORY.CATEGORY_TABLE_FIELD.IS_ACTIVE,
      headerName: CERTIFICATION_CATEGORY.CATEGORY_TABLE_HEADER.ENABLE_DISABLE,
      headerClassName: 'action-column',
      cellClassName: 'action-cell',
      width: 120,
      renderCell: (params) => renderActionCell(params),
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const columns = window.innerWidth <= 768 ? mobileColumns : desktopColumns;

  const sortFilterColumns = [
    columns[0]?.field,
    columns[1]?.field,
  ];

  const content = selectedRole?.isActive
    ? ENABLE_DISABLE_CATEGORY_DIALOG.DISABLE
    : ENABLE_DISABLE_CATEGORY_DIALOG.ENABLE;

  return (
    <div className="cert-master-outer-layer ">
      <div className="certification-master">
        <h1 className="certification-master-title">
          {CERTIFICATION_CATEGORY.TITLE}
        </h1>
        <Filter
          filterData={rows}
          buttonText={CERTIFICATION_CATEGORY.ADD_BUTTON_TEXT}
          placeHolder={CERTIFICATION_CATEGORY.PLACE_HOLDER}
          selectedCategories={selectedCategories}
          selectedSubCategories={selectedSubCategories}
          setSelectedCategories={setSelectedCategories}
          setSelectedSubCategories={setSelectedSubCategories}
          updateCategoriesList={updateCategoriesList}
          isEdit={isEdit}
        />
        {loading && (
          <div
            className="loading"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px',
            }}
          >
            <img
              src={PuzzleLoader}
              alt="Loading..."
              className="loader-gif"
              style={{ width: '100px', height: 'auto' }}
            />
          </div>
        )}
        <div className="certificate-data-grid-container">
          <DataGridComponent
            dataRows={updatedRows}
            isFilteredRows={newRows}
            dataColumns={isMobileView ? mobileColumns : desktopColumns}
            sortFilterColumns={sortFilterColumns}
            pageSize={10}
            isEdit={isEdit}
          />
          <EnableDisableCategoryDialog
            isDialogOpen={isDialogOpen}
            selectedRow={selectedRole}
            handleCloseAction={handleDialogClose}
            handleEnableDisableAction={handleDialogSubmit}
            setIsDialogOpen={setIsDialogOpen}
            setRows={setNewRows}
            handleSnackbar={enableDisableCategorySnackbar}
            setIsEdit={setIsEdit}
            content={content}
          />
          <SimpleSnackbar // SnackBar to handle response for enable/disable role & download report
            open={openSnackbar}
            message={snackbarMessage}
            severity={severity}
            alertCss={snackBarCss}
          />
          <EditCategoryForm // edit popup common component
            modalOpen={openEditModal}
            handleClose={() => {
              setOpenEditModal(false);
              setIsEdit(false);
            }}
            setRows={setRows}
            isEdit={isEdit}
            selectedCategoryId={selectedCategoryId}
            selectedSubCategoryId={selectedSubCategoryId}
            selectedcategory={categoryName}
            selectedSubCategory={subCategoryName}
            isActive={isActive}
            lastUpdatedBy={userProfile?.user_name}
          />
        </div>
      </div>
    </div>
  );
};

export default CertificationCategory;
